import { SubmitEventHandler, use, useState } from 'react';
import { useHistory } from 'react-router';

import { toast, ToastProps } from '@motorway/mw-highway-code/pre-alpha';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { GA_VARIABLES } from 'Utilities/analytics';
import { DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsDocumentCapture';
import { EVENT } from 'Utilities/analytics/optimizely/constants';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';
import { DPEnquiry } from 'Utilities/vehicles/@types';

import { confirmTransaction, getConfirmSaleStatus, postAcceptOffer } from 'Services/privateApi';

import { OfferPropTypes } from '../../../acceptOffer/AcceptOffer.types';
import { DocumentCaptureConfirmationProgressContext } from '../../../documentCapture/DocumentCaptureContexts';
import { captureSentryException } from '../../../documentCapture/DocumentCaptureError/captureSentryException';
import { getIsReservePriceMet } from '../../DealerRatingsExperiment/DealerRatingsExperiment.helpers';

const setGAValue = (value: string) => {
  GA_VARIABLES.offerAcceptedValue = value;
};

type useAcceptAndConfirmSaleProps = {
  errorProps: Pick<ToastProps, 'heading' | 'content'>;
  offer: OfferPropTypes;
  vehicleEnquiry: DPEnquiry;
};

export const useAcceptAndConfirmSale = ({ errorProps, offer, vehicleEnquiry }: useAcceptAndConfirmSaleProps) => {
  const { vehicleLookupState: vehicle } = use(VehicleLookupContext);
  const { setProgress } = use(DocumentCaptureConfirmationProgressContext);

  const [loading, setLoading] = useState(false);
  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();
  const history = useHistory();

  const isAboveReserve = getIsReservePriceMet({ offer, vehicleEnquiry });
  const { closingBid, enquiryId } = offer;
  const bidValue = String(offer.closingBid?.value);

  const confirmSale = async () => {
    await confirmTransaction(enquiryId);

    setGAValue(bidValue);
    DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.CONFIRM_SALE_BUTTON_CLICKED({ label: 'Complete my sale' });
    DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.CONFIRM_SALE_BUTTON_ACCEPTED_VALUE({ label: bidValue });
    OPTIMIZELY_TRACK(EVENT.PS_COMPLETE_SALE_CTA_CLICKED);
    OPTIMIZELY_TRACK(EVENT.PS_COMPLETE_SALE_CTA_CLICKED, { customId: String(enquiryId) });

    const data = await getConfirmSaleStatus(enquiryId);
    setProgress?.(data);

    history.push(`/${offer.vehicleDetails?.vrm}/add-bank-details`);
  };

  const acceptOffer = async () => {
    DOCUMENT_CAPTURE_SNOW_PLOW_EVENTS.ACCEPT_OFFER_BUTTON_CLICK();
    OPTIMIZELY_TRACK(EVENT.PS_ACCEPT_OFFER_CTA_CLICKED);
    OPTIMIZELY_TRACK(EVENT.PS_ACCEPT_OFFER_CTA_CLICKED, {
      customId: String(vehicle?.enquiry?.dpEnquiryId),
    });

    return postAcceptOffer({ closingBidId: closingBid?.id, enquiryId }).then(confirmSale);
  };

  const onSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    setLoading(true);

    (isAboveReserve ? confirmSale : acceptOffer)()
      .catch((error: unknown) => {
        toast({ ...errorProps, variant: 'error' });
        captureSentryException(error);
        setLoading(false);
      });
  };

  return {
    loading,
    onSubmit,
  };
};
